
import { Component, Prop } from 'vue-property-decorator';
import Vue from 'vue';
import { StoreModel } from '@/models/store';
import { TaxTypeModel } from '@/models/tax_type';
import { TaxTypeList } from '@/collections/tax_types';
import { get, post, put, del } from '@/services/http';
import { EventBus } from '@/eventBus';

@Component
export default class TaxTypeSettings extends Vue {
  @Prop({ type: Object, required: true }) store!: StoreModel;

  private loading = false;
  private saving = false;
  private deleting = false;
  private taxTypes: TaxTypeList = new TaxTypeList();

  // Form dialog state
  private formDialog = false;
  private formValid = false;
  private isEditing = false;
  private editingTaxType: TaxTypeModel | null = null;
  private formData = {
    name: '',
    rate: null as number | null,
    description: ''
  };

  // Delete dialog state
  private deleteDialog = false;
  private deletingTaxType: TaxTypeModel | null = null;

  get defaultTaxTypeId(): number | null {
    const defaultType = this.activeTaxTypes.find(t => t.isDefault);
    return defaultType ? defaultType.id : null;
  }

  get nameRules() {
    return [
      (v: string) => !!v || 'Name is required',
      (v: string) => (v && v.length <= 50) || 'Name must be 50 characters or less',
      (v: string) => this.isNameUnique(v) || 'A tax type with this name already exists'
    ];
  }

  get rateRules() {
    return [
      (v: number | null) => v !== null && v !== undefined && String(v) !== '' || 'Rate is required',
      (v: number) => (v >= 0 && v <= 100) || 'Rate must be between 0 and 100'
    ];
  }

  get descriptionRules() {
    return [
      (v: string) => !v || v.length <= 200 || 'Description must be 200 characters or less'
    ];
  }

  get activeTaxTypes(): TaxTypeModel[] {
    return this.taxTypes.items.filter(t => t.isActive);
  }

  private isNameUnique(name: string): boolean {
    if (!name) return true;
    const normalized = name.trim().toLowerCase();
    return !this.activeTaxTypes.some(t =>
      t.id !== this.editingTaxType?.id &&
      t.name.toLowerCase() === normalized
    );
  }

  formatRate(rate: number): string {
    return `${(rate * 100).toFixed(1)}%`;
  }

  async mounted() {
    await this.fetchTaxTypes();
  }

  async fetchTaxTypes() {
    this.loading = true;
    try {
      await this.taxTypes.fetch();
    } catch (error) {
      console.error('Failed to load tax types:', error);
      EventBus.$emit('show-snackbar', { text: 'Failed to load tax types', color: 'error' });
    } finally {
      this.loading = false;
    }
  }

  openCreateDialog() {
    this.isEditing = false;
    this.editingTaxType = null;
    this.formData = { name: '', rate: null, description: '' };
    this.formDialog = true;
  }

  openEditDialog(taxType: TaxTypeModel) {
    this.isEditing = true;
    this.editingTaxType = taxType;
    this.formData = {
      name: taxType.name,
      rate: taxType.rate * 100,
      description: taxType.description
    };
    this.formDialog = true;
  }

  closeFormDialog() {
    this.formDialog = false;
    this.editingTaxType = null;
    (this.$refs.taxTypeForm as any)?.reset?.();
  }

  async saveTaxType() {
    if (!this.formValid) return;

    this.saving = true;
    try {
      const payload = {
        name: this.formData.name.trim(),
        rate: (this.formData.rate ?? 0) / 100,
        description: this.formData.description.trim()
      };

      if (this.isEditing && this.editingTaxType) {
        await put(`/tax-types/${this.editingTaxType.id}`, payload);
        EventBus.$emit('show-snackbar', { text: 'Tax type updated', color: 'success' });
      } else {
        await post('/tax-types', payload);
        EventBus.$emit('show-snackbar', { text: 'Tax type created', color: 'success' });
      }

      this.closeFormDialog();
      await this.fetchTaxTypes();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to save tax type';
      EventBus.$emit('show-snackbar', { text: message, color: 'error' });
    } finally {
      this.saving = false;
    }
  }

  async setDefault(taxType: TaxTypeModel) {
    if (taxType.id === this.defaultTaxTypeId) return;

    try {
      await put(`/tax-types/${taxType.id}`, { isDefault: true });
      // Update local state
      this.taxTypes.items.forEach(t => {
        t.isDefault = t.id === taxType.id;
      });
      EventBus.$emit('show-snackbar', { text: `${taxType.name} set as default`, color: 'success' });
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to set default';
      EventBus.$emit('show-snackbar', { text: message, color: 'error' });
    }
  }

  confirmDelete(taxType: TaxTypeModel) {
    this.deletingTaxType = taxType;
    this.deleteDialog = true;
  }

  async deleteTaxType() {
    if (!this.deletingTaxType) return;

    this.deleting = true;
    try {
      await del(`/tax-types/${this.deletingTaxType.id}`);
      EventBus.$emit('show-snackbar', { text: 'Tax type deleted', color: 'success' });
      this.deleteDialog = false;
      this.deletingTaxType = null;
      await this.fetchTaxTypes();
    } catch (error: any) {
      const message = error.response?.data?.message || 'Failed to delete tax type';
      EventBus.$emit('show-snackbar', { text: message, color: 'error' });
    } finally {
      this.deleting = false;
    }
  }
}
